import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { segmentApi } from '@edulastic/api'
import { EduJoyride } from '@edulastic/common'
import { tutorials } from '@edulastic/constants'
import { ACTIONS, EVENTS, LIFECYCLE } from 'react-joyride'
import { getProfileReportsJoyrideVisit } from '../ducks'
import { getUserId } from '../../src/selectors/user'
import {
  PROFILE_REPORTS_JOYRIDE_SEGMENT_CTA,
  PROFILE_REPORTS_JOYRIDE_TARGET,
  PROFILE_REPORTS_JOYRIDE_START_DELAY_MS,
  markProfileReportsJoyrideVisit1CompletedThisSession,
  profileReportsJoyrideStepsVisit1,
  profileReportsJoyrideStepsVisit2,
} from '../constants'
import {
  scrollJoyrideTargetIntoView,
  waitForStableJoyrideTarget,
} from '../utils/profileReportsJoyrideUtils'
import { stopJoyrideThen } from '../utils/dashboardJoyrideUtils'
import { setViewedTutorialsAction } from '../../../tutorials/tutorialReducer'

const PROFILE_REPORTS_JOYRIDE_LOCALE = {
  close: 'Okay, got it',
}

const ProfileReportsJoyride = ({ visit, userId, setViewedTutorials }) => {
  const [isTourReady, setIsTourReady] = useState(false)
  const [run, setRun] = useState(false)
  const teardownTimerRef = useRef(null)
  const isVisit1 = visit === 1

  useEffect(
    () => () => {
      if (teardownTimerRef.current) {
        clearTimeout(teardownTimerRef.current)
      }
    },
    []
  )

  useEffect(() => {
    if (!visit) {
      setIsTourReady(false)
      setRun(false)
      return undefined
    }

    let cancelled = false
    let startTimer

    setIsTourReady(false)
    setRun(false)

    waitForStableJoyrideTarget(PROFILE_REPORTS_JOYRIDE_TARGET).then(
      (target) => {
        if (cancelled || !target) return

        scrollJoyrideTargetIntoView(target)
        startTimer = setTimeout(() => {
          if (!cancelled) {
            setIsTourReady(true)
            setRun(true)
          }
        }, PROFILE_REPORTS_JOYRIDE_START_DELAY_MS)
      }
    )

    return () => {
      cancelled = true
      clearTimeout(startTimer)
      setIsTourReady(false)
      setRun(false)
    }
  }, [visit])

  const teardownTour = useCallback((then) => {
    if (teardownTimerRef.current) {
      clearTimeout(teardownTimerRef.current)
    }
    teardownTimerRef.current = stopJoyrideThen(
      () => setRun(false),
      () => {
        then?.()
        setIsTourReady(false)
      }
    )
  }, [])

  const profileReportsJoyrideCallback = useCallback(
    (data) => {
      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        teardownTour()
        return
      }

      if (data.lifecycle !== LIFECYCLE.COMPLETE) return

      const { CLOSE, NEXT, LAST } = ACTIONS
      const isCtaComplete = data.action === NEXT || data.action === LAST
      const isClose = data.action === CLOSE

      if (isVisit1) {
        if (!isCtaComplete && !isClose) return
        teardownTour(() => {
          markProfileReportsJoyrideVisit1CompletedThisSession(userId)
          setViewedTutorials([tutorials.profileReportsJoyrideVisit1])
          segmentApi.genericEventTrack('Joyride', {
            userResponse: 'clickCTA',
            CTA_Name: PROFILE_REPORTS_JOYRIDE_SEGMENT_CTA,
            pagePath: window.location.pathname,
            popup_occurrence: 1,
          })
        })
      } else {
        if (!isClose && !isCtaComplete) return
        teardownTour(() => {
          setViewedTutorials([tutorials.profileReportsJoyrideVisit2])
          segmentApi.genericEventTrack('Joyride', {
            userResponse: isClose ? 'Close' : 'clickCTA',
            CTA_Name: PROFILE_REPORTS_JOYRIDE_SEGMENT_CTA,
            pagePath: window.location.pathname,
            popup_occurrence: 2,
          })
        })
      }
    },
    [isVisit1, setViewedTutorials, teardownTour, userId]
  )

  if (!visit || !isTourReady) {
    return null
  }

  return (
    <EduJoyride
      hideCloseButton={isVisit1}
      run={run}
      disableScrolling
      steps={
        isVisit1
          ? profileReportsJoyrideStepsVisit1
          : profileReportsJoyrideStepsVisit2
      }
      callback={profileReportsJoyrideCallback}
      locale={PROFILE_REPORTS_JOYRIDE_LOCALE}
      styles={{
        options: { width: isVisit1 ? 360 : 352, zIndex: 1016 },
        tooltipTitle: { display: 'none' },
        tooltipFooter: {
          justifyContent: 'flex-start',
          marginTop: 16,
        },
        tooltipFooterSpacer: {
          display: 'none',
        },
        buttonNext: {
          fontWeight: 700,
        },
      }}
      options={{ zIndex: 1016 }}
    />
  )
}

export default connect(
  (state) => ({
    visit: getProfileReportsJoyrideVisit(state),
    userId: getUserId(state),
  }),
  {
    setViewedTutorials: setViewedTutorialsAction,
  }
)(ProfileReportsJoyride)
